import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { LifeBuoyIcon, UserIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useSupportStaffClient } from '@/entities/support-staff';

export const WidgetsSupportStaffMain = () => {
  const { t } = useTranslation('supportStaff');
  const { data, isLoading, isError, error, refetch } = useSupportStaffClient();

  const staff = data ?? [];

  return (
    <>
      <PageHeader title={t('title')} showBack backTo="/settings" />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState message={extractErrorMessage(error)} onRetry={() => void refetch()} />
        ) : staff.length === 0 ? (
          <EmptyState
            icon={<LifeBuoyIcon className="h-10 w-10" aria-hidden />}
            title={t('emptyTitle')}
            description={t('emptyDescription')}
          />
        ) : (
          staff.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-faint">
                {item.image ? (
                  <img src={item.image} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserIcon className="h-7 w-7" aria-hidden />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold text-ink">{item.name}</p>
                {item.experience_years != null && (
                  <p className="truncate text-xs text-ink-muted">
                    {t('experience', { years: item.experience_years })}
                  </p>
                )}
                {item.languages.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {item.languages.map((language) => (
                      <span
                        key={language}
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <a
                href={item.contact_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-brand-600 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-700 active:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                {t('contact')}
              </a>
            </div>
          ))
        )}
      </div>
    </>
  );
};
